import styled, { css } from "styled-components";
import { ColorV2 } from "config/colors";
import { TypographyV2 } from "config/font";
import { Tag } from "components/designV2/tag/tag";
import { media } from "components/designV2/breakpoints";

export const RowCard = styled.div`
  background: ${ColorV2.White};
  border-radius: 24px;
  padding: 16px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  max-width: 100%;
  margin-top: 16px;
`;

export const scrollEdgeFadeStyle = css`
  &::before,
  &::after {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    width: 30px;
    pointer-events: none;
    z-index: 4;
    opacity: 0;
    transition: opacity 160ms ease;
  }

  &::before {
    left: 0;
    background: linear-gradient(
      to right,
      rgba(15, 23, 42, 0.08),
      rgba(15, 23, 42, 0)
    );
  }

  &::after {
    right: 0;
    background: linear-gradient(
      to left,
      rgba(15, 23, 42, 0.08),
      rgba(15, 23, 42, 0)
    );
  }

  &:has([data-can-scroll-left="true"])::before {
    opacity: 1;
  }

  &:has([data-can-scroll-right="true"])::after {
    opacity: 1;
  }
`;

export const RowScrollCard = styled.div`
  position: relative;
  isolation: isolate;
  background: ${ColorV2.White};
  border-radius: 24px;
  padding: 16px;
  margin-top: 16px;
  width: 100%;
  overflow: hidden;

  ${scrollEdgeFadeStyle}
`;

export const RowScrollArea = styled.div`
  overflow-x: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

export const MonthGridRow = styled.div<{ $dayCount: number }>`
  display: grid;
  grid-template-columns: 160px repeat(${(props) => props.$dayCount}, 40px);
  column-gap: 12px;
  align-items: center;
  width: 100%;

  ${media.desktop} {
    grid-template-columns: 220px repeat(
        ${(props) => props.$dayCount},
        minmax(28px, 1fr)
      );
    column-gap: 4px;
  }
`;

export const ScheduleRowLayout = styled.div<{ $dayCount: number }>`
  display: grid;
  grid-template-columns: 240px repeat(
      ${(props) => props.$dayCount},
      minmax(68px, 1fr)
    );
  column-gap: 16px;
  align-items: center;
`;

const fadeWidth = "16px";
export const VerticalFade = styled.div`
  position: absolute;
  top: 0;
  right: -${fadeWidth};
  bottom: 0;
  width: ${fadeWidth};
  background: linear-gradient(to right, ${ColorV2.White} 10%, transparent);
  pointer-events: none;
`;

export const StickyNameCell = styled.div<{ $isSticky: boolean }>`
  position: ${(props) => (props.$isSticky ? "sticky" : "static")};
  left: 0;
  align-self: stretch;
  background: ${ColorV2.White};
  z-index: 3;
  width: 100%;
  min-width: 0;
`;

export const RowInfo = styled.div`
  display: flex;
  align-items: center;
`;

export const RowSectionMeta = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding-left: 58px;
  color: ${ColorV2.Blue4};
`;

export const RowSectionRole = styled.span`
  ${TypographyV2.s1};
  color: ${ColorV2.Blue4};
`;

export const ScheduleTag = styled(Tag)`
  max-width: 80px;
  overflow: hidden;

  > span {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const MonthScheduleTag = styled(ScheduleTag)`
  max-width: 44px;
`;

export const MonthInfo = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 12px;
`;

export const MonthSectionMeta = styled.div`
  display: inline-flex;
  align-items: center;
  min-width: 0;
`;

export const MonthAvatar = styled.div<{ $backgroundColor?: ColorV2 }>`
  flex: 0 0 32px;
  min-width: 32px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${(props) => props.$backgroundColor ?? ColorV2.White};
  border: 1px solid ${ColorV2.Blue6};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

export const MonthAvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const MonthName = styled.div`
  ${TypographyV2.b1};
  color: ${ColorV2.Blue1};
  flex: 1 1 auto;
  min-width: 0;
  overflow-wrap: anywhere;
`;

export const MonthInfoSpacer = styled.div`
  height: 32px;
`;

type ShiftBlockSize = "small" | "large" | "pill";

export const CellHoverAction = styled.button.attrs({
  "data-cell-hover": "true",
})<{ $size: ShiftBlockSize }>`
  ${TypographyV2.s1};
  position: relative;
  border: none;
  background: ${ColorV2.Blue6};
  color: ${ColorV2.Blue4};
  border-radius: 16px;
  opacity: 0;
  pointer-events: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  transition: opacity 0.15s ease;
  cursor: pointer;

  ${(props) =>
    props.$size === "large" &&
    css`
      ${TypographyV2.b1};
      width: 100%;
      padding: 24px 8px;
    `}

  ${(props) =>
    props.$size === "small" &&
    css`
      ${TypographyV2.b1};
      width: 100%;
      padding: 4px 8px;
      overflow-wrap: anywhere;
    `}

	${(props) =>
    props.$size === "pill" &&
    css`
      width: 24px;
      height: 26px;
      border-radius: 8px;
    `}
`;

export const ScheduleRowCell = styled.div<{
  $justify?: "center" | "flex-start";
  $offset?: number;
}>`
  display: flex;
  align-items: center;
  justify-content: ${(props) => props.$justify ?? "center"};
  margin-left: ${(props) => (props.$offset ? `${props.$offset}px` : "0")};
  min-width: 0;
  position: relative;

  &:hover [data-cell-hover="true"] {
    opacity: 0.5;
    pointer-events: auto;
  }
`;
